import { Col, Row, Space } from "antd";
import { useState } from "react";
import {
  useDashboardSatesByClickQuery,
  useDashboardSatesQuery,
} from "../../../../features/superAdmin/dashboard/dashboardApi";
import { addCommaInNumber } from "../../../../utils/number";
import { DFilters } from "../Dashboard";
import ApprovedTicketIcon from "../_icons/ApprovedTicketIcon";
import AssignedDepartmentIcon from "../_icons/AssignedDepartmentIcon";
import AssignedToMeIcon from "../_icons/AssignedToMeIcon";
import CanceledTicketIcon from "../_icons/CanceledTicketIcon";
import CheckedTicketIcon from "../_icons/CheckedTicketIcon";
import ClosedTicketIcon from "../_icons/ClosedTicketIcon";
import CreatedByMeIcon from "../_icons/CreatedByMeIcon";
import ExecutedTicketIcon from "../_icons/ExecutedTicketIcon";
import InProgressTicketIcon from "../_icons/InProgressTicketIcon";
import MyDepartmentIcon from "../_icons/MyDepartmentIcon";
import OpenedTicketIcon from "../_icons/OpenedTicketIcon";
import RejectedTicketIcon from "../_icons/RejectedTicketIcon";
import ResolvedTicketIcon from "../_icons/ResolvedTicketIcon";
import TotalTicketIcon from "../_icons/TotalTicketIcon";
import WithLoader from "./WithLoader";

type Config = {
  icon: React.JSX.Element;
  wrapperClass?: string;
  title: string;
  content: number | string;
  activeWrapperClass?: string;
};
export type TSelected =
  | "my-ticket"
  | "my-department"
  | "assigned-to-me"
  | "assigned-to-my-department";
// my-ticket, my-department, assigned-to-me, assigned-to-my-department
const States = ({ toDate, fromDate, skip, limit, ...filter }: DFilters) => {
  const [selected, setSelected] = useState<TSelected>("my-ticket");
  const { data, isLoading, isFetching } = useDashboardSatesQuery({
    params: {
      ...filter,
      fromDate: fromDate ? fromDate : undefined,
      toDate: toDate ? toDate : undefined,
    },
  });
  const {
    data: statesByClick,
    isLoading: loading,
    isFetching: fetching,
  } = useDashboardSatesByClickQuery({
    params: {
      ...filter,
      fromDate: fromDate ? fromDate : undefined,
      toDate: toDate ? toDate : undefined,
      card_name: selected,
    },
  });

  const {
    totalRaisedTickets,
    assignedToMe,
    myDepartmentTickets,
    assignToMyDepartment,
  } = data?.data || {};
  const totalTickets = (myDepartmentTickets || 0) + (assignToMyDepartment || 0);

  const {
    openedTickets,
    inProgressTickets,
    checkedTickets,
    approvedTickets,
    executedTickets,
    rejectedTickets,
    resolvedTickets,
    cancelledTickets,
    closedTickets,
  } = statesByClick?.data || {};

  const roleBasedConfig: (Config & { key: TSelected })[] = [
    {
      key: "my-ticket",
      icon: <CreatedByMeIcon />,
      wrapperClass: "bg-[#F9FCFF]", // very light sky blue
      activeWrapperClass: "bg-[#D1ECFF]", // deeper blue
      title: "Tickets Created by Me",
      content: addCommaInNumber(totalRaisedTickets || 0),
    },
    {
      key: "my-department",
      icon: <MyDepartmentIcon />,
      wrapperClass: "bg-[#FFFCF8]", // very light peach/orange
      activeWrapperClass: "bg-[#FFE5D1]", // deeper orange
      title: "My Department Tickets",
      content: addCommaInNumber(myDepartmentTickets || 0),
    },
    {
      key: "assigned-to-me",
      icon: <AssignedToMeIcon />,
      wrapperClass: "bg-[#FAFFFD]", // very light mint green
      activeWrapperClass: "bg-[#C9FFE2]", // deeper green
      title: "Tickets Assigned to Me",
      content: addCommaInNumber(assignedToMe || 0),
    },
    {
      key: "assigned-to-my-department",
      icon: <AssignedDepartmentIcon />,
      wrapperClass: "bg-[#FBF8FF]", // very light lavender
      activeWrapperClass: "bg-[#E0D2FF]", // deeper purple
      title: "Assigned to My Department",
      content: addCommaInNumber(assignToMyDepartment || 0),
    },
  ];

  const config: Config[] = [
    {
      icon: <TotalTicketIcon />,
      title: "Total Tickets",
      content: addCommaInNumber(totalTickets || 0),
    },
    {
      icon: <OpenedTicketIcon />,
      title: "Opened Tickets",
      content: addCommaInNumber(openedTickets || 0),
    },
    {
      icon: <InProgressTicketIcon />,
      title: "In Progress Tickets",
      content: addCommaInNumber(inProgressTickets || 0), //Todo: Need to provide real data
    },
    {
      icon: <ResolvedTicketIcon />,
      title: "Resolved Tickets",
      content: addCommaInNumber(resolvedTickets || 0),
    },
    {
      icon: <CheckedTicketIcon />,
      title: "Checked Tickets",
      content: addCommaInNumber(checkedTickets || 0),
    },
    {
      icon: <ApprovedTicketIcon />,
      title: "Approved Tickets",
      content: addCommaInNumber(approvedTickets || 0),
    },
    {
      icon: <ExecutedTicketIcon />,
      title: "Executed Tickets",
      content: addCommaInNumber(executedTickets || 0),
    },
    {
      icon: <RejectedTicketIcon />,
      title: "Rejected Tickets",
      content: addCommaInNumber(rejectedTickets || 0),
    },
    {
      icon: <ClosedTicketIcon />,
      title: "Closed Tickets",
      content: addCommaInNumber(closedTickets || 0),
    },
    {
      icon: <CanceledTicketIcon />,
      title: "Canceled Tickets",
      content: addCommaInNumber(cancelledTickets || 0),
    },
  ];

  return (
    <div className="space-y-6">
      <WithLoader isLoading={isLoading || isFetching}>
        <Row gutter={[24, 24]}>
          {roleBasedConfig?.map((item) => (
            <Col key={item.title} span={6}>
              <button
                key={item.title}
                className="w-full text-start"
                onClick={() => {
                  setSelected((prev) => (prev === item.key ? prev : item.key));
                }}
              >
                <State
                  isActive={item.key === selected}
                  item={item}
                  key={item.title}
                />
              </button>
            </Col>
          ))}
        </Row>
      </WithLoader>
      <WithLoader isLoading={isLoading || isFetching || loading || fetching}>
        <Row gutter={[24, 24]}>
          {config?.map((item) => (
            <Col key={item.title} span={6}>
              <State item={item} key={item.title} />
            </Col>
          ))}
        </Row>
      </WithLoader>
    </div>
  );
};

const State = ({ item, isActive }: { item: Config; isActive?: boolean }) => {
  return (
    <div
      className={`border-none p-4 rounded-lg space-y-2 ${
        isActive ? item?.activeWrapperClass : item?.wrapperClass
      } shadow`}
    >
      <Space>
        {item.icon}
        <h6 className="text-base font-normal text-display-body">
          {item.title}
        </h6>
      </Space>
      <h4 className="text-[28px] text-display-body font-normal">
        {item.content}
      </h4>
    </div>
  );
};

export default States;
